
    import { computed, defineComponent, onBeforeMount, ref, watch } from 'vue'

    import { useGtag } from '@/common/composables/libs/gtag'
    import { useI18n } from 'vue-i18n-composable'
    import { Song } from '@/common/models/data/dashboard/song'
    import type { Schedule } from '@/common/models/data/dashboard/schedule/schedule'
    import { radioBroadcastService } from '@/common/json-ld/media'

    import PlayerControls from '@/app/components/sidebar/player/PlayerControls.vue'
    import PlayerInfo from '@/app/components/sidebar/player/PlayerInfo.vue'
    import PlayerVolume from '@/app/components/sidebar/player/PlayerVolume.vue'

    import { usePlayerStore } from '@/app/stores/player'
    import { useScheduleStore } from '@/app/stores/schedule'

    export default defineComponent({
        components: {
            PlayerControls,
            PlayerInfo,
            PlayerVolume
        },
        props: {
            // TODO: Remove prop, can be read from sidebar store
            appDiscovered: {
                type: Boolean,
                default: false
            },
            shown: {
                type: Boolean,
                required: true
            }
        },
        emits: {
            'song-changed': (value: unknown): boolean => {
                return value instanceof Song
            }
        },
        setup (props, { emit }) {
            const gtag = useGtag()
            const i18n = useI18n()

            const playerStore = usePlayerStore()
            const scheduleStore = useScheduleStore()

            const audio = ref<HTMLAudioElement>()
            const buffering = ref(false)
            const currentProgram = ref<Schedule>()

            const cover = computed(() => currentProgram.value?.player_image ?? null)
            const coverAlt = computed(() => currentProgram.value?.title ?? i18n.t('globals.organization').toString())
            const currentSong = computed(() => playerStore.currentSong)
            const playing = computed(() => playerStore.playing)
            const streamSource = computed(() => playerStore.streamSource)

            const jsonLd = computed(() => radioBroadcastService(streamSource.value))

            const onToggle = async (): Promise<void> => {
                if (playing.value) {
                    audio.value?.pause()

                    gtag.event(i18n.t('analytics.player.stop').toString(), {
                        event_category: 'Player'
                    })
                } else {
                    buffering.value = true

                    // Force reload to download actual stream content
                    try {
                        audio.value?.load()
                        await audio.value?.play()
                    } finally {
                        buffering.value = false
                    }

                    gtag.event(i18n.t('analytics.player.play').toString(), {
                        event_category: 'Player'
                    })
                }

                playerStore.togglePlaying()
            }

            const onVolumeChange = (volume: number): void => {
                if (audio.value) {
                    audio.value.volume = volume
                }
            }

            watch(currentSong, value => emit('song-changed', value))

            onBeforeMount(async (): Promise<void> => {
                await Promise.all([
                    playerStore.loadStreamUrl(),
                    playerStore.loadMetadata()
                ])

                playerStore.noDataString = i18n.t('player.no-data').toString()
                currentProgram.value = scheduleStore.currentProgramme

                window.setInterval(async () => {
                    await playerStore.loadMetadata()
                }, 10 * 1000)

                window.setInterval(() => {
                    currentProgram.value = scheduleStore.currentProgramme
                }, 60 * 1000)
            })

            return {
                audio,
                buffering,
                cover,
                coverAlt,
                currentProgram,
                currentSong,
                jsonLd,
                playing,
                streamSource,
                onToggle,
                onVolumeChange
            }
        }
    })
